<template>
  <div class="image-field">
    <label class="block text-sm font-medium text-gray-700 mb-1">{{ label }}</label>

    <!-- Current image preview -->
    <div v-if="modelValue?.url" class="mb-2">
      <div class="relative group rounded border border-gray-200 overflow-hidden bg-gray-50">
        <img
          :src="modelValue.url"
          :alt="modelValue.alternativeText || ''"
          class="w-full h-32 object-contain"
        />
        <button
          class="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
          @click="clearImage"
          title="Remove image"
        >
          <i class="pi pi-times" style="font-size: 0.65rem"></i>
        </button>
      </div>
      <div class="mt-1">
        <InputText
          :model-value="modelValue.alternativeText || ''"
          class="w-full text-xs"
          placeholder="Alt text..."
          @input="updateAltText(($event.target as HTMLInputElement).value)"
        />
      </div>
    </div>

    <!-- No image state -->
    <div v-else class="mb-2 p-4 border border-dashed border-gray-300 rounded bg-gray-50 text-center text-sm text-gray-500">
      No image selected
    </div>

    <!-- Action buttons -->
    <div class="flex space-x-2">
      <Button
        label="Upload"
        icon="pi pi-upload"
        size="small"
        outlined
        :loading="uploadMutation.isPending.value"
        @click="triggerFileInput"
        class="flex-1 text-xs"
      />
      <Button
        label="Browse"
        icon="pi pi-images"
        size="small"
        outlined
        @click="showBrowser = true"
        class="flex-1 text-xs"
      />
    </div>

    <!-- Hidden file input -->
    <input
      ref="fileInputRef"
      type="file"
      accept="image/*"
      class="hidden"
      @change="handleFileUpload"
    />

    <!-- Media browser dialog -->
    <Dialog
      v-model:visible="showBrowser"
      header="Media Library"
      modal
      :style="{ width: '700px', maxHeight: '80vh' }"
    >
      <div v-if="mediaQuery.isLoading.value" class="text-center py-8 text-gray-500">
        Loading media...
      </div>
      <div v-else-if="!mediaItems.length" class="text-center py-8 text-gray-500">
        No media uploaded yet. Use the Upload button to add images.
      </div>
      <div v-else class="grid grid-cols-4 gap-3 max-h-96 overflow-y-auto p-1">
        <div
          v-for="asset in mediaItems"
          :key="asset.id"
          class="cursor-pointer rounded border-2 overflow-hidden hover:border-blue-500 transition-colors"
          :class="[selectedAssetId === asset.id ? 'border-blue-500 ring-2 ring-blue-200' : 'border-gray-200']"
          @click="selectAsset(asset)"
        >
          <img
            :src="asset.url"
            :alt="asset.altText || asset.originalFilename"
            class="w-full h-24 object-cover"
          />
          <div class="p-1 text-xs text-gray-600 truncate">
            {{ asset.originalFilename }}
          </div>
        </div>
      </div>
      <template #footer>
        <Button
          label="Cancel"
          text
          size="small"
          @click="showBrowser = false"
        />
        <Button
          label="Select"
          size="small"
          :disabled="!selectedAssetId"
          @click="confirmSelection"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import InputText from 'primevue/inputtext'
import { useProjectMediaQuery, useUploadMediaMutation, type MediaAssetResponse } from '@/composables/useMedia'
import type { ImageAsset } from '@aannemerplus/types'

interface Props {
  modelValue: ImageAsset | null
  label: string
  projectId: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: ImageAsset | null]
}>()

// State
const showBrowser = ref(false)
const selectedAssetId = ref<number | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)

// Media data
const mediaQuery = useProjectMediaQuery(computed(() => props.projectId))
const uploadMutation = useUploadMediaMutation()

const mediaItems = computed<MediaAssetResponse[]>(() => {
  const items = mediaQuery.data.value || []
  // Only show image types in the picker
  return items.filter(a => a.mimeType.startsWith('image/'))
})

// Actions
const triggerFileInput = () => {
  fileInputRef.value?.click()
}

const handleFileUpload = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file || !props.projectId) return

  try {
    const result = await uploadMutation.mutateAsync({
      projectId: props.projectId,
      file,
    })

    emit('update:modelValue', {
      url: result.url,
      alternativeText: result.altText || '',
      seoAlt: result.altText || '',
    })
  } catch (err) {
    console.error('Upload failed:', err)
  }

  // Reset file input
  input.value = ''
}

const selectAsset = (asset: MediaAssetResponse) => {
  selectedAssetId.value = asset.id
}

const confirmSelection = () => {
  const asset = mediaItems.value.find(a => a.id === selectedAssetId.value)
  if (!asset) return

  emit('update:modelValue', {
    url: asset.url,
    alternativeText: asset.altText || '',
    seoAlt: asset.altText || '',
  })

  showBrowser.value = false
  selectedAssetId.value = null
}

const clearImage = () => {
  emit('update:modelValue', null)
}

const updateAltText = (altText: string) => {
  if (!props.modelValue) return
  emit('update:modelValue', {
    ...props.modelValue,
    alternativeText: altText,
    seoAlt: altText,
  })
}
</script>
